import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';
import ControleDeFrequencia from '../../components/controle_frequencia';
import {
    Typography, Container, Box, CircularProgress, Alert, Paper,
    Button, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
    TextField, IconButton, Avatar, Snackbar, Fade, Divider, useTheme
} from '@mui/material';
import {
    ArrowBack,
    Grading,
    Save as SaveIcon,
    AssignmentTurnedIn as AssignmentIcon,
    Person as PersonIcon
} from '@mui/icons-material';

const AvaliacaoPagina = () => {
    const { turmaId, grupoId, alunoId, casoId, avaliacaoId } = useParams();
    const navegar = useNavigate();
    const theme = useTheme();
    const modoEdicao = !!avaliacaoId;
    const [aluno, setAluno] = useState(null);
    const [caso, setCaso] = useState(null);
    const [criterios, setCriterios] = useState([]);
    const [opcoes, setOpcoes] = useState([]);
    const [respostas, setRespostas] = useState({});
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'info' });
    const [periodo, setPeriodo] = useState(null);
    const [dadosFrequencia, setDadosFrequencia] = useState(null);
    const [idsDinamicos, setIdsDinamicos] = useState({
        idGrupo: grupoId,
        idCaso: casoId,
        idTurma: turmaId
    });

    const [presencasSalvas, setPresencasSalvas] = useState(null);

    const buscarDados = useCallback(async () => {
        try {
            setCarregando(true);
            setErro('');

            let dadosDoCasoCompleto;
            let idAluno, idCaso, idGrupo, idTurma;

            if (modoEdicao) {
                const respAvaliacao = await api.get(`/api/avaliacoes/${avaliacaoId}`);
                const { cabecalho, respostas: respostasSalvas, frequencia } = respAvaliacao.data;

                idAluno = cabecalho.aluno_id;
                idCaso = cabecalho.caso_clinico_id;
                idGrupo = cabecalho.grupo_tutoria_id;
                idTurma = cabecalho.turma_id;

                if (frequencia && frequencia.presencas) {
                    setPresencasSalvas(frequencia.presencas);
                    setDadosFrequencia(frequencia);
                }

                setIdsDinamicos({
                    idGrupo,
                    idCaso,
                    idTurma
                });

                const respDetalhes = await api.get(`/api/grupos/${idGrupo}/casos/${idCaso}/detalhes`);
                dadosDoCasoCompleto = respDetalhes.data;

                setAluno({ id: cabecalho.aluno_id, nome_completo: cabecalho.aluno_nome });
                setCaso({ id: cabecalho.caso_clinico_id, titulo: cabecalho.caso_titulo });

                const respOpcoes = await api.get('/api/opcoes-resposta');
                setOpcoes(respOpcoes.data);

                setCriterios(dadosDoCasoCompleto.criteriosParaAvaliar.map(c => ({
                    ...c,
                    tipo: c.tipo || 'PADRAO'
                })));

                const respostasIniciais = {};
                respostasSalvas.forEach(item => {
                    respostasIniciais[item.criterio_id] = {
                        opcao_resposta_id: String(item.opcao_resposta_id),
                        observacao_criterio: item.observacao_criterio,
                    };
                });
                setRespostas(respostasIniciais);

                setPeriodo(dadosDoCasoCompleto.periodo || null);

            } else {
                idAluno = alunoId;
                idCaso = casoId;
                idGrupo = grupoId;
                idTurma = turmaId;

                const respDetalhes = await api.get(`/api/grupos/${idGrupo}/casos/${idCaso}/detalhes`);
                dadosDoCasoCompleto = respDetalhes.data;

                const respAluno = await api.get(`/api/usuarios/${idAluno}`);

                setAluno(respAluno.data);
                setCaso(dadosDoCasoCompleto.caso);
                setCriterios(dadosDoCasoCompleto.criteriosParaAvaliar.map(c => ({
                    ...c,
                    tipo: c.tipo || 'PADRAO'
                })));
                setOpcoes(dadosDoCasoCompleto.opcoesDeResposta);
                setPeriodo(dadosDoCasoCompleto.periodo || null);
            }
        } catch (err) {
            setErro('Não foi possível carregar os dados para a avaliação.');
            console.error(err);
        } finally {
            setCarregando(false);
        }
    }, [avaliacaoId, modoEdicao, alunoId, casoId, grupoId, turmaId]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    const lidarComMudancaDeNota = (criterioId, opcaoRespostaId) => {
        setRespostas(prev => ({
            ...prev,
            [criterioId]: { ...prev[criterioId], opcao_resposta_id: opcaoRespostaId }
        }));
    };

    const lidarComMudancaDeObservacao = (criterioId, observacao) => {
        setRespostas(prev => ({
            ...prev,
            [criterioId]: { ...prev[criterioId], observacao_criterio: observacao }
        }));
    };

    const handleCloseFeedback = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setFeedback({ ...feedback, open: false });
        if (feedback.severity === 'success') {
            navegar(`/grupos/${idsDinamicos.idGrupo}/casos/${idsDinamicos.idCaso}`);
        }
    };

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setFeedback({ open: false, message: '', severity: 'info' });

        const respostasArray = Object.keys(respostas)
            .filter(criterioId => {
                const criterio = criterios.find(c => c.id === parseInt(criterioId));
                return criterio && criterio.tipo !== 'FREQUENCIA';
            })
            .map(criterioId => ({
                criterio_id: parseInt(criterioId, 10),
                opcao_resposta_id: parseInt(respostas[criterioId]?.opcao_resposta_id, 10),
                observacao_criterio: respostas[criterioId]?.observacao_criterio || ''
            }));

        const criteriosPadrao = criterios.filter(c => c.tipo === 'PADRAO');
        const todasRespondidas = criteriosPadrao.every(c =>
            respostasArray.some(r => r.criterio_id === c.id && r.opcao_resposta_id)
        );

        if (criteriosPadrao.length > 0 && !todasRespondidas) {
            return setErro("Por favor, preencha a nota para todos os critérios padrão.");
        }

        try {
            if (modoEdicao) {
                const payload = {
                    respostas: respostasArray,
                    dados_frequencia: dadosFrequencia
                };
                await api.put(`/api/avaliacoes/${avaliacaoId}`, payload);
                setFeedback({ open: true, message: 'Avaliação ATUALIZADA com sucesso!', severity: 'success' });
            } else {
                const payload = {
                    aluno_id: parseInt(alunoId, 10),
                    caso_clinico_id: parseInt(casoId, 10),
                    turma_id: parseInt(turmaId, 10),
                    grupo_id: parseInt(grupoId, 10),
                    respostas: respostasArray,
                    dados_frequencia: dadosFrequencia
                };
                await api.post('/api/avaliacoes/criar', payload);
                setFeedback({ open: true, message: 'Avaliação SALVA com sucesso!', severity: 'success' });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.mensagem || "Ocorreu um erro ao salvar a avaliação.";
            setFeedback({ open: true, message: errorMessage, severity: 'error' });
            setErro(errorMessage);
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (erro && !aluno) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    {erro}
                </Alert>
            </Container>
        );
    }

    return (
        <>
            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={handleCloseFeedback}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseFeedback}
                    severity={feedback.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: feedback.severity === 'success' ? cores.sucesso.principal : cores.erro.principal
                    }}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
                <Fade in={true} timeout={500}>
                    <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                        <Box component="form" onSubmit={lidarComSubmissao}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4,
                                p: { xs: 2, sm: 3 },
                                borderRadius: 2,
                                background: cores.gradientes.primario,
                                color: 'white'
                            }}>
                                <IconButton
                                    onClick={() => navegar(`/grupos/${idsDinamicos.idGrupo}/casos/${idsDinamicos.idCaso}`)}
                                    sx={{
                                        mr: 2,
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)',
                                        }
                                    }}
                                >
                                    <ArrowBack />
                                </IconButton>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="h4"
                                        component="h1"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {modoEdicao ? 'Editar Avaliação' : 'Formulário de Avaliação'}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}
                                    >
                                        Preencha os critérios para avaliar o aluno
                                    </Typography>
                                </Box>
                            </Box>

                            <Fade in={true} timeout={600}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        mb: 3,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 2,
                                        border: `1px solid ${cores.cinza.claro}`,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                                            <Avatar
                                                sx={{
                                                    mr: 2,
                                                    bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                    color: cores.primaria.principal
                                                }}
                                            >
                                                <AssignmentIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                                    CASO CLÍNICO
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                                    {caso?.titulo}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                        <Divider orientation="horizontal" sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }} />

                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                                            <Avatar
                                                sx={{
                                                    mr: 2,
                                                    bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                    color: cores.primaria.principal
                                                }}
                                            >
                                                <PersonIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                                    ALUNO
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                                    {aluno?.nome_completo}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Fade>

                            {criterios.map((criterio, index) => {
                                if (criterio.tipo === 'FREQUENCIA') {
                                    return (
                                        <Fade
                                            key={criterio.id}
                                            in={true}
                                            timeout={700 + (index * 100)}
                                        >
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: { xs: 2, sm: 3 },
                                                    my: 3,
                                                    background: '#f8f9fa',
                                                    borderRadius: 2,
                                                    border: `1px solid ${cores.cinza.claro}`,
                                                    boxShadow: cores.sombras.suave,
                                                    transition: 'box-shadow 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: cores.sombras.medio
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Avatar
                                                        sx={{
                                                            mr: 2,
                                                            bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                            color: cores.secundaria.principal
                                                        }}
                                                    >
                                                        <Grading />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            variant="overline"
                                                            sx={{
                                                                color: cores.cinza.medio,
                                                                fontWeight: 600,
                                                                display: 'block',
                                                                letterSpacing: 1.2
                                                            }}
                                                        >
                                                            FREQUÊNCIA {index + 1}
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: cores.secundaria.principal,
                                                                fontSize: '1.1rem'
                                                            }}
                                                        >
                                                            {criterio.descricao}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Divider sx={{ my: 2, opacity: 0.6 }} />

                                                <Box sx={{
                                                    pl: { xs: 0, sm: '56px' },
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center'
                                                }}>
                                                    <Box sx={{
                                                        width: '100%',
                                                        maxWidth: '500px'
                                                    }}>
                                                        <Box
                                                            sx={{
                                                                mb: 3,
                                                                p: 2.5,
                                                                borderRadius: 2,
                                                                bgcolor: `rgba(${parseInt(cores.sucesso.principal.slice(1, 3), 16)}, ${parseInt(cores.sucesso.principal.slice(3, 5), 16)}, ${parseInt(cores.sucesso.principal.slice(5, 7), 16)}, 0.1)`,
                                                                border: `1px solid rgba(${parseInt(cores.sucesso.principal.slice(1, 3), 16)}, ${parseInt(cores.sucesso.principal.slice(3, 5), 16)}, ${parseInt(cores.sucesso.principal.slice(5, 7), 16)}, 0.2)`,
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: cores.primaria.principal,
                                                                    mb: 0.5
                                                                }}
                                                            >
                                                                Marque a presença do aluno em cada aula do período selecionado
                                                            </Typography>

                                                        </Box>

                                                        <ControleDeFrequencia
                                                            periodo={periodo}
                                                            presencasSalvas={presencasSalvas}
                                                            onChange={(dados) => setDadosFrequencia(prev => {
                                                                return {
                                                                    ...prev,
                                                                    presencas: dados.presencas
                                                                };
                                                            })}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Fade>
                                    );
                                } else {
                                    return (
                                        <Fade
                                            key={criterio.id}
                                            in={true}
                                            timeout={700 + (index * 100)}
                                        >
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: { xs: 2, sm: 3 },
                                                    my: 3,
                                                    background: '#f8f9fa',
                                                    borderRadius: 2,
                                                    border: `1px solid ${cores.cinza.claro}`,
                                                    boxShadow: cores.sombras.suave,
                                                    transition: 'box-shadow 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: cores.sombras.medio
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Avatar
                                                        sx={{
                                                            mr: 2,
                                                            bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                            color: cores.primaria.principal
                                                        }}
                                                    >
                                                        <Grading />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            variant="overline"
                                                            sx={{
                                                                color: cores.cinza.medio,
                                                                fontWeight: 600,
                                                                display: 'block',
                                                                letterSpacing: 1.2
                                                            }}
                                                        >
                                                            CRITÉRIO {index + 1}
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: cores.primaria.principal,
                                                                fontSize: '1.1rem'
                                                            }}
                                                        >
                                                            {criterio.descricao}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Divider sx={{ my: 2, opacity: 0.6 }} />

                                                <Box sx={{ pl: { xs: 0, sm: '56px' } }}>
                                                    <FormControl component="fieldset" fullWidth>
                                                        <FormLabel
                                                            component="legend"
                                                            sx={{
                                                                mb: 1,
                                                                color: cores.cinza.escuro,
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            Selecione a nota para este critério:
                                                        </FormLabel>
                                                        <RadioGroup
                                                            value={respostas[criterio.id]?.opcao_resposta_id || ''}
                                                            onChange={(e) => lidarComMudancaDeNota(criterio.id, e.target.value)}
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: { xs: 'column', sm: 'row' },
                                                                flexWrap: 'wrap',
                                                                gap: 1
                                                            }}
                                                        >
                                                            {opcoes.map(opcao => (
                                                                <FormControlLabel
                                                                    key={opcao.id}
                                                                    value={String(opcao.id)}
                                                                    control={
                                                                        <Radio
                                                                            sx={{
                                                                                color: cores.cinza.medio,
                                                                                '&.Mui-checked': {
                                                                                    color: cores.primaria.principal,
                                                                                }
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={opcao.texto_opcao}
                                                                    sx={{
                                                                        bgcolor: respostas[criterio.id]?.opcao_resposta_id === String(opcao.id) ? `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.05)` : 'transparent',
                                                                        borderRadius: 1,
                                                                        px: 1,
                                                                        py: 0.5,
                                                                        transition: 'background-color 0.2s ease',
                                                                        mr: 1
                                                                    }}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    </FormControl>

                                                    <TextField
                                                        label="Observações sobre este critério"
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        margin="normal"
                                                        variant="outlined"
                                                        value={respostas[criterio.id]?.observacao_criterio || ''}
                                                        onChange={(e) => lidarComMudancaDeObservacao(criterio.id, e.target.value)}
                                                        InputProps={{
                                                            sx: { borderRadius: 2 }
                                                        }}
                                                        placeholder="Adicione comentários ou observações sobre a avaliação deste critério..."
                                                        helperText="Suas observações ajudarão o aluno a entender sua avaliação"
                                                    />
                                                </Box>
                                            </Paper>
                                        </Fade>
                                    );
                                }
                            })}

                            {erro && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mt: 2,
                                        borderRadius: 2,
                                        backgroundColor: cores.erro.claro,
                                        borderColor: cores.erro.medio,
                                        color: cores.erro.escuro,
                                        '& .MuiAlert-icon': {
                                            alignItems: 'center'
                                        }
                                    }}
                                >
                                    {erro}
                                </Alert>
                            )}

                            <Box sx={{
                                mt: 4,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 2,
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navegar(`/grupos/${idsDinamicos.idGrupo}/casos/${idsDinamicos.idCaso}`)}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 3,
                                        borderColor: cores.cinza.medio,
                                        color: cores.cinza.escuro,
                                        '&:hover': {
                                            borderColor: cores.cinza.escuro,
                                            bgcolor: 'rgba(71, 85, 105, 0.04)'
                                        }
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 3,
                                        bgcolor: cores.primaria.principal,
                                        fontWeight: 600,
                                        boxShadow: cores.sombras.suave,
                                        '&:hover': {
                                            bgcolor: cores.primaria.escuro,
                                        }
                                    }}
                                >
                                    {modoEdicao ? 'Salvar Alterações' : 'Salvar Avaliação'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>
            </Container>
        </>
    );
};

export default AvaliacaoPagina;